
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";

import Loader from "../../components/Loader";
import {
    buildSign,
    deleteSign,
    duplicateSign,
    exportSignJson,
    fetchSignFile,
    getSignMeta,
    importSignJson,
    listSigns,
    readApiError,
    readJsonFile,
} from "./printSignApi";
import { JsonImportButton, formatBytes, formatDateTime } from "./signUi";
import { ActionsMenu, PageMessage, SIGNS_ROOT, SignsPage, StatusBadge } from "./signPage";

/*
 * Вывески: список, как у смет и КП. Отсюда — в редактор, сборка PDF,
 * последние файлы, копия, JSON, удаление. Профили бренда и библиотека — кнопками сверху.
 */

const fileName = (sign, file, format) => (
    `${(sign.name || "Вывеска").replace(/[\\/:*?"<>|]+/g, " ").trim()} ${file.widthMm}x${file.heightMm}.${format}`
);

const PrintSignsListPage = () => {
    const navigate = useNavigate();
    const [signs, setSigns] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState({});
    const [expanded, setExpanded] = useState("");
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [nextSigns, nextMeta] = await Promise.all([listSigns(), getSignMeta()]);
            setSigns(nextSigns);
            setMeta(nextMeta);
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить вывески") });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const designLabel = (key) => (meta?.designs || []).find((design) => design.key === key)?.label || "Гранит и золото";

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return signs;
        return signs.filter((sign) => [sign.name, sign.profileName].some((value) => String(value || "").toLowerCase().includes(needle)));
    }, [query, signs]);

    /** Действие над вывеской: пока идёт — в кнопке «Действия» видно, что именно. */
    const run = async (sign, label, task, errorText) => {
        setBusy((current) => ({ ...current, [sign._id]: label }));
        setMessage(null);
        try {
            await task();
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, errorText) });
        } finally {
            setBusy((current) => {
                const next = { ...current };
                delete next[sign._id];
                return next;
            });
        }
    };

    const replace = (next) => setSigns((current) => current.map((item) => (item._id === next._id ? { ...item, ...next } : item)));

    const build = (sign) => run(sign, "Собираем…", async () => {
        const next = await buildSign(sign._id);
        replace(next);
        const warnings = next.files[0]?.warnings || [];
        setMessage({ type: warnings.length ? "info" : "success", text: `«${next.name}» собрана.${warnings.length ? ` ${warnings.join(" ")}` : ""}` });
    }, "Не удалось собрать вывеску");

    const openFile = (sign, file, format) => {
        // Окно открываем сразу (иначе браузер заблокирует), файл подставляем, когда скачается.
        const win = window.open("", "_blank");
        run(sign, "Открываем…", async () => {
            try {
                const blob = await fetchSignFile(sign._id, file._id, format);
                const url = URL.createObjectURL(blob);
                if (win) win.location.href = url;
                else window.open(url, "_blank");
            } catch (error) {
                win?.close();
                throw error;
            }
        }, "Не удалось открыть файл");
    };

    const downloadFile = (sign, file, format) => run(sign, "Скачиваем…", async () => {
        saveAs(await fetchSignFile(sign._id, file._id, format), fileName(sign, file, format));
    }, "Не удалось скачать файл");

    const copy = (sign) => run(sign, "Копируем…", async () => {
        const next = await duplicateSign(sign._id);
        setSigns((current) => [next, ...current]);
        setMessage({ type: "success", text: `Готова копия «${next.name}».` });
    }, "Не удалось скопировать");

    const exportJson = (sign) => run(sign, "Выгружаем…", async () => {
        const { blob, filename } = await exportSignJson(sign._id);
        saveAs(blob, filename);
    }, "Не удалось выгрузить JSON");

    const remove = (sign) => {
        if (!window.confirm(`Удалить вывеску «${sign.name}»? Собранные файлы тоже удалятся. Профиль бренда останется.`)) return;
        run(sign, "Удаляем…", async () => {
            await deleteSign(sign._id);
            setSigns((current) => current.filter((item) => item._id !== sign._id));
        }, "Не удалось удалить");
    };

    const [importing, setImporting] = useState(false);
    const importJson = async (file) => {
        setImporting(true);
        setMessage(null);
        try {
            const result = await importSignJson({ data: await readJsonFile(file) });
            if (result.sign) {
                navigate(`${SIGNS_ROOT}/${result.sign._id}`);
                return;
            }
            setMessage({ type: "success", text: `Из «${file.name}» создан профиль бренда.` });
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить JSON") });
        } finally {
            setImporting(false);
        }
    };

    return (
        <SignsPage
            title="Вывески"
            subtitle="Вывески на дверь и фасад: PDF для печати и PNG для мессенджеров"
            back="/"
        >
            <div className="signsPage__toolbar">
                <label className="signsSearch">
                    <span aria-hidden="true">⌕</span>
                    <input type="text" value={query} placeholder="Поиск по названию, профилю…" onChange={(event) => setQuery(event.target.value)} />
                </label>
                <button type="button" className="signsButton" onClick={() => navigate("/admin/print-cards")}>Визитки</button>
                <button type="button" className="signsButton" onClick={() => navigate(`${SIGNS_ROOT}/profiles`)}>Профили бренда</button>
                <button type="button" className="signsButton" onClick={() => navigate(`${SIGNS_ROOT}/library`)}>Библиотека картинок</button>
                <JsonImportButton className="signsButton" disabled={importing} onFile={importJson} label={importing ? "Загрузка…" : "Импорт JSON"} />
                <button type="button" className="signsButton is-primary" onClick={() => navigate(`${SIGNS_ROOT}/new`)}>+ Новая вывеска</button>
            </div>

            <PageMessage message={message} onClose={() => setMessage(null)} />

            <div className="signsTable">
                <div className="signsTable__row is-head">
                    <span>#</span>
                    <span>Название</span>
                    <span>Профиль бренда</span>
                    <span>Размер · дизайн</span>
                    <span>Обновлено</span>
                    <span>Статус</span>
                    <span className="is-right">Действия</span>
                </div>

                {loading ? <div className="signsTable__empty"><Loader /></div> : null}
                {!loading && !filtered.length ? (
                    <div className="signsTable__empty">
                        {signs.length ? "Ничего не нашлось." : "Вывесок пока нет — нажмите «+ Новая вывеска» или загрузите JSON."}
                    </div>
                ) : null}

                {filtered.map((sign, index) => {
                    const last = sign.files[0];
                    const isOpen = expanded === sign._id;
                    return (
                        <div key={sign._id} className={`signsTable__group${isOpen ? " is-open" : ""}`}>
                            <div className="signsTable__row">
                                <span className="signsTable__num">{index + 1}</span>
                                <button type="button" className="signsTable__name" onClick={() => navigate(`${SIGNS_ROOT}/${sign._id}`)}>
                                    <i className={`printSigns__swatch is-${sign.design}`} />
                                    <strong>{sign.name || "Вывеска"}</strong>
                                </button>
                                <span className="signsTable__muted">{sign.profileName || "—"}</span>
                                <span className="signsTable__muted">{sign.size.widthMm} × {sign.size.heightMm} мм · {designLabel(sign.design)}</span>
                                <span>{formatDateTime(sign.updatedAt).split(",")[0] || "—"}</span>
                                <span>
                                    {last ? <StatusBadge tone="success">собрана</StatusBadge> : <StatusBadge>черновик</StatusBadge>}
                                    {sign.files.length ? <StatusBadge tone="count">{sign.files.length} PDF</StatusBadge> : null}
                                </span>
                                <span className="is-right">
                                    <ActionsMenu
                                        busy={busy[sign._id]}
                                        disabled={!!busy[sign._id]}
                                        items={[
                                            { label: "Открыть редактор", onClick: () => navigate(`${SIGNS_ROOT}/${sign._id}`) },
                                            { label: last ? "Собрать PDF заново" : "Собрать PDF", onClick: () => build(sign) },
                                            last && { label: "Открыть последний PDF", divider: true, onClick: () => openFile(sign, last, "pdf") },
                                            last && { label: "Скачать последний PDF", onClick: () => downloadFile(sign, last, "pdf") },
                                            last?.hasPng && { label: "Скачать PNG для мессенджеров", onClick: () => downloadFile(sign, last, "png") },
                                            sign.files.length && { label: isOpen ? "Скрыть файлы" : "Показать все файлы", onClick: () => setExpanded(isOpen ? "" : sign._id) },
                                            { label: "Сделать копию", divider: true, onClick: () => copy(sign) },
                                            { label: "Экспорт JSON", onClick: () => exportJson(sign) },
                                            { label: "Удалить", danger: true, divider: true, onClick: () => remove(sign) },
                                        ]}
                                    />
                                </span>
                            </div>

                            {isOpen ? (
                                <div className="signsFiles">
                                    <div className="signsFiles__title">Файлы ({sign.files.length})</div>
                                    {sign.files.map((file) => (
                                        <div key={file._id} className="signsFiles__row">
                                            <div>
                                                <strong>{fileName(sign, file, "pdf")}</strong>
                                                <span>
                                                    {formatDateTime(file.createdAt)} · PDF {formatBytes(file.pdfBytes)}
                                                    {file.bleedMm ? ` · вылеты ${file.bleedMm} мм` : ""} · Собрал: {file.createdByName || "—"}
                                                </span>
                                            </div>
                                            <div className="signsFiles__actions">
                                                <button type="button" className="signsButton" onClick={() => openFile(sign, file, "pdf")}>Открыть</button>
                                                <button type="button" className="signsButton" onClick={() => downloadFile(sign, file, "pdf")}>Скачать PDF</button>
                                                {file.hasPng ? <button type="button" className="signsButton" onClick={() => downloadFile(sign, file, "png")}>PNG</button> : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </SignsPage>
    );
};

export default PrintSignsListPage;
